import { useMsal } from "@azure/msal-react";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { assetsQueryOptions } from "../services/assets/assetsQueryOptions";
import { buildFacetGraph } from "../services/buildFacetGraph";
import { buildDateFilters } from "../services/dateFilter";
import { fetchFacets, OVERLAY_MAX_RESULT } from "../services/fetchFacets";
import { buildAssetDocsGraph } from "../services/graph/buildAssetDocsGraph";
import { buildAssetGraph } from "../services/graph/buildAssetGraph";
import {
    locksToFilters,
    SUB_FACETS_STALE_TIME,
    subFacetsQueryOptions,
} from "../services/subFacetsQuery";
import type {
    Cluster,
    CountBucket,
    FacetField,
    SelectableField,
    UseOverviewGraphParams,
} from "../types";

const MAX_CLUSTERS = 8;

const toBuckets = (facets: { key: string; doc_count: number }[]): CountBucket[] =>
    facets.map((facet) => ({ value: facet.key, count: facet.doc_count }));

const bucketsForField = (fields: FacetField[] | undefined, field: string): CountBucket[] =>
    toBuckets(fields?.find((entry) => entry.field === field)?.facets ?? []);

const cleanBuckets = (buckets: CountBucket[]): CountBucket[] =>
    buckets.filter((bucket) => bucket.value.trim().length > 0).sort((a, b) => b.count - a.count);

export const buildClusters = (
    facets: FacetField[] | undefined,
    fields: SelectableField[],
    excludedFields: Set<string>,
): Cluster[] =>
    fields
        .filter((field) => !excludedFields.has(field.value))
        .map((field) => {
            const buckets = bucketsForField(facets, field.value)
                .filter((bucket) => bucket.value.trim().length > 0)
                .sort((a, b) => b.count - a.count);
            return {
                field: field.value,
                label: field.label,
                buckets,
                distinctCount: buckets.length,
                total: buckets.reduce((sum, bucket) => sum + bucket.count, 0),
            };
        })
        .filter((candidate) => candidate.buckets.length > 0)
        .sort((a, b) => b.total - a.total)
        .slice(0, MAX_CLUSTERS)
        .map((candidate) => ({
            field: candidate.field,
            label: candidate.label,
            buckets: candidate.buckets,
            distinctCount: candidate.distinctCount,
        }));

export const useOverviewGraph = ({
    index,
    rootLabel,
    category,
    categoryLabel,
    fields,
    gridPage,
    selected,
    overlay,
    dateField,
    dateRange,
    assetMode,
    focusedAssetId,
}: UseOverviewGraphParams) => {
    const { instance } = useMsal();

    const dateFilters = useMemo(
        () => buildDateFilters(dateField, dateRange),
        [dateField, dateRange],
    );
    const dateFiltersKey = dateFilters.flatMap((filter) => [filter.key, ...filter.value]);

    const categoriesQuery = useQuery({
        queryKey: ["overview-categories", index, ...dateFiltersKey],
        enabled: Boolean(index),
        staleTime: SUB_FACETS_STALE_TIME,
        refetchOnWindowFocus: false,
        placeholderData: (previous) => previous,
        queryFn: () =>
            fetchFacets({ instance, index, filters: dateFilters, maxResult: OVERLAY_MAX_RESULT }),
    });

    const subEnabled = Boolean(index) && Boolean(selected) && !assetMode;

    const subFacetsQuery = useQuery({
        ...subFacetsQueryOptions({
            instance,
            index,
            locks: selected ? [selected] : [],
            extraFilters: dateFilters,
        }),
        enabled: subEnabled,
        refetchOnWindowFocus: false,
        placeholderData: (previous) => previous,
    });

    const subLoading =
        subEnabled &&
        (subFacetsQuery.isPlaceholderData ||
            (subFacetsQuery.fetchStatus === "fetching" && !subFacetsQuery.data));

    const categoriesBuckets = useMemo(
        () => cleanBuckets(bucketsForField(categoriesQuery.data?.fields, category)),
        [categoriesQuery.data, category],
    );

    const fieldOptions = useMemo(() => {
        if (category) return undefined;
        return fields.map((field) => ({
            field: field.value,
            label: field.label,
            count: cleanBuckets(bucketsForField(categoriesQuery.data?.fields, field.value)).length,
        }));
    }, [category, fields, categoriesQuery.data]);

    const clustersData = useMemo(
        () =>
            selected && !subLoading
                ? buildClusters(subFacetsQuery.data?.fields, fields, new Set([selected.field]))
                : [],
        [selected, subLoading, subFacetsQuery.data, fields],
    );

    const overlayBase = useMemo(() => {
        if (!overlay) return [];
        if (overlay.field === category) return cleanBuckets(categoriesBuckets);
        return clustersData.find((cluster) => cluster.field === overlay.field)?.buckets ?? [];
    }, [overlay, category, categoriesBuckets, clustersData]);

    const overlayLocks = overlay && overlay.field !== category && selected ? [selected] : [];
    const overlayLocksKey = overlayLocks.flatMap((lock) => [lock.field, lock.value]).join("|");

    const overlayQuery = useQuery({
        queryKey: [
            "overview-overlay",
            index,
            overlay?.field ?? "",
            overlayLocksKey,
            ...dateFiltersKey,
        ],
        enabled: Boolean(index) && Boolean(overlay),
        staleTime: SUB_FACETS_STALE_TIME,
        refetchOnWindowFocus: false,
        queryFn: () =>
            fetchFacets({
                instance,
                index,
                filters: [...locksToFilters(overlayLocks), ...dateFilters],
                maxResult: OVERLAY_MAX_RESULT,
            }),
    });

    const overlayBuckets = useMemo(() => {
        if (!overlay) return [];
        if (overlayQuery.data)
            return cleanBuckets(bucketsForField(overlayQuery.data.fields, overlay.field));
        return overlayBase;
    }, [overlay, overlayQuery.data, overlayBase]);

    const assetsEnabled = Boolean(index) && Boolean(assetMode) && Boolean(selected) && !overlay;

    const assetsQuery = useQuery({
        ...assetsQueryOptions({ instance, index, selected, dateFilters }),
        enabled: assetsEnabled,
        refetchOnWindowFocus: false,
        placeholderData: (previous) => previous,
    });

    const assetsLoading =
        assetsEnabled &&
        (assetsQuery.isPlaceholderData ||
            (assetsQuery.fetchStatus === "fetching" && !assetsQuery.data));
    const assets = useMemo(() => assetsQuery.data ?? [], [assetsQuery.data]);
    const focusedAsset = useMemo(
        () => assets.find((asset) => asset.id === focusedAssetId),
        [assets, focusedAssetId],
    );

    const assetChain = useMemo(
        () =>
            selected
                ? [{ label: rootLabel }, { label: selected.value, caption: categoryLabel }]
                : [{ label: rootLabel }],
        [rootLabel, selected, categoryLabel],
    );

    const assetFlow = useMemo(() => {
        if (!assetsEnabled || !selected) return undefined;
        if (focusedAsset) return buildAssetDocsGraph({ asset: focusedAsset, chain: assetChain });
        return buildAssetGraph({
            assets,
            chain: assetChain,
            assetCount: categoriesBuckets.find((bucket) => bucket.value === selected.value)?.count,
            loading: assetsLoading,
        });
    }, [
        assetsEnabled,
        selected,
        focusedAsset,
        assets,
        assetChain,
        categoriesBuckets,
        assetsLoading,
    ]);

    const flow = useMemo(() => {
        if (assetFlow) return assetFlow;
        return buildFacetGraph({
            categories: categoriesBuckets,
            categoryField: category,
            categoryLabel,
            rootLabel,
            gridPage,
            selected,
            clusters: clustersData,
            subLoading,
            overlay: overlay ? { ...overlay, buckets: overlayBuckets } : undefined,
            fieldOptions,
        });
    }, [
        categoriesBuckets,
        clustersData,
        category,
        categoryLabel,
        gridPage,
        selected,
        rootLabel,
        subLoading,
        overlay,
        overlayBuckets,
        fieldOptions,
        assetFlow,
    ]);

    const noSubGroups =
        !assetMode &&
        Boolean(selected) &&
        !subLoading &&
        subFacetsQuery.isSuccess &&
        clustersData.length === 0;

    return {
        data: flow,
        isLoading: categoriesQuery.isLoading,
        isError: categoriesQuery.isError || subFacetsQuery.isError,
        noSubGroups,
        subLoading,
    };
};
